import { FC, useEffect, useRef, useState, useMemo, useCallback, memo } from 'react'
import JoditEditor from 'jodit-react'
import Select from 'react-select'
import imageCompression from 'browser-image-compression'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import { buildDropdownAudit, config, fetchData, fetchFormData, toasterrormsg, toastsuccessmsg } from '../../../../reusable'
import { KTIcon, toAbsoluteUrl } from '../../../../_metronic/helpers'
import { SelectOption, StepCommonProps } from '../../types/ads'

const adsDetailSchema = Yup.object().shape({
  en_name: Yup.string().max(100, 'Max 100 Characters').required('English Title Is Required'),
  ru_name: Yup.string().max(100, 'Max 100 Characters').required('Russian Title Is Required'),
  uz_name: Yup.string().max(100, 'Max 100 Characters').required('Uzbek Title Is Required'),
  en_description: Yup.string().required('English Description Is Required'),
  ru_description: Yup.string().required('Russian Description Is Required'),
  uz_description: Yup.string().required('Uzbek Description Is Required'),
  promocode: Yup.string().max(20, 'Max 20 Characters'),
})

// Memoized editor — only re-renders when initialValue changes (not on every formik keystroke)
const MemoizedEditor = memo(({ initialValue, onBlur, editorConfig }: {
  initialValue: string
  onBlur: (content: string) => void
  editorConfig: any
}) => {
  const ref = useRef(null)
  return <JoditEditor ref={ref} config={editorConfig} value={initialValue} onBlur={onBlur} />
}, (prev, next) => prev.initialValue === next.initialValue)

interface Props extends StepCommonProps {
  goToNext: () => void
}

const MAX_GALLERY = 6

// `detailApi` empty means the category has no sub-items: the property picked from
// admin/propertylist is itself what gets promoted, so no detail call is made.
const CATEGORY_TYPES = [
  { value: 1, label: 'Accommodation', parentApi: 'admin/vendorList', detailApi: 'vendor/public/hotelDetailsV2', subKey: 'roomList', parentIdKey: 'hotelId', subIdKey: 'roomId', nameKey: 'en_roomName' },
  { value: 3, label: 'Cafe & Restaurant', parentApi: 'admin/vendorList', detailApi: '', subKey: '', parentIdKey: 'restaurantId', subIdKey: '', nameKey: 'en_name' },
  { value: 4, label: 'Space Rental', parentApi: 'admin/vendorList', detailApi: 'vendor/public/spaceDetails', subKey: 'rentablespace', parentIdKey: 'spacerentalId', subIdKey: 'rentablespaceId', nameKey: 'en_name' },
  // tourpackageDetails takes the parent tour package as `tourpackageId`; its toursDetail[] items are keyed by `toursId`.
  { value: 5, label: 'Tour Package', parentApi: 'admin/vendorList', detailApi: 'vendor/public/tourpackageDetails', subKey: 'toursDetail', parentIdKey: 'tourpackageId', subIdKey: 'toursId', nameKey: 'en_name' },
  { value: 7, label: 'Car Rental', parentApi: 'admin/vendorList', detailApi: 'vendor/public/carRentalDetail', subKey: 'vehcileDetail', parentIdKey: 'carrentalId', subIdKey: 'vehicleId', nameKey: 'en_name' },
]

// actionType sent to the API, keyed by the vendor's categoryId. Banner ads promote the
// sub-item, so the names are item-level (room / vehicle) rather than property-level —
// except for restaurants, which have no sub-item and are promoted as the property itself.
const ACTION_TYPE_BY_CATEGORY: Record<number, string> = {
  1: 'room',
  3: 'restaurant',
  4: 'space',
  5: 'tour',
  7: 'vehicle',
}

// The vendor's categoryId decides which property type is promoted. actionTypeParent /
// actionType are only hints and come back empty until the step has been saved once, so
// they are used purely as a fallback — never with an empty value, which would match
// every label via includes('') and silently fall back to the first category.
const resolveCategoryType = (ad: any): { value: number; label: string } | null => {
  const categoryId = Number(ad?.categoryId) || 0
  const byCategory = CATEGORY_TYPES.find((c) => c.value === categoryId)
  if (byCategory) return { value: byCategory.value, label: byCategory.label }

  const hint = (ad?.actionTypeParent || ad?.actionType || '').toLowerCase().trim()
  if (hint) {
    const byHint = CATEGORY_TYPES.find((c) => c.label.toLowerCase().includes(hint))
    if (byHint) return { value: byHint.value, label: byHint.label }
  }

  // Category without a sub-item configuration — still let the main property be picked.
  return categoryId ? { value: categoryId, label: 'Property' } : null
}

const AdsDetailStep: FC<Props> = ({ t, adsId, adminId, vendorId, adData, imageBaseUrl, onSaveSuccess, goToNext }) => {
  const editorConfig = useMemo(() => config(300), [])

  // Media
  const [existingMedia, setExistingMedia] = useState<{ adsmediaId: number; image: string }[]>([])
  const [mediaUploading, setMediaUploading] = useState(false)

  // Promotion cascading dropdowns
  const [selectedCategoryType, setSelectedCategoryType] = useState<SelectOption | null>(null)
  const [vendorPropertyList, setVendorPropertyList] = useState<SelectOption[]>([])
  const [selectedProperty, setSelectedProperty] = useState<SelectOption | null>(null)
  const [subItemList, setSubItemList] = useState<SelectOption[]>([])
  const [selectedSubItems, setSelectedSubItems] = useState<(string | number)[]>([])
  const initialSubItemListRef = useRef<SelectOption[]>([])
  const [propertyLoading, setPropertyLoading] = useState(false)
  const [subItemLoading, setSubItemLoading] = useState(false)
  const [noSubItems, setNoSubItems] = useState(false)
  const [subItemError, setSubItemError] = useState('')

  const [saving, setSaving] = useState(false)

  // Derived from the category, not stored: the API returns null until this step saves,
  // so echoing it back would keep it empty forever.
  const actionType = useMemo(() => {
    const catId = Number(selectedCategoryType?.value) || Number(adData?.categoryId) || 0
    return ACTION_TYPE_BY_CATEGORY[catId] ?? adData?.actionType ?? ''
  }, [selectedCategoryType?.value, adData?.categoryId, adData?.actionType])

  const totalImages = existingMedia.length

  const getCategoryLabel = useCallback((): string => {
    if (!selectedCategoryType) return 'Property'
    return selectedCategoryType.label || 'Property'
  }, [selectedCategoryType])

  const getSubItemLabel = useCallback((): string => {
    const catId = Number(selectedCategoryType?.value)
    if (catId === 1) return 'Room'
    if (catId === 4) return 'Space'
    if (catId === 5) return 'Tour Package'
    if (catId === 7) return 'Car'
    return 'Item'
  }, [selectedCategoryType])

  // Formik for text fields
  const formik = useFormik({
    initialValues: {
      en_name: '',
      ru_name: '',
      uz_name: '',
      en_description: '',
      ru_description: '',
      uz_description: '',
      promocode: '',
    },
    validationSchema: adsDetailSchema,
    onSubmit: async (values) => {
      if (!adsId) { toasterrormsg('Please Complete Previous Steps First'); return }
      if (existingMedia.length === 0) { toasterrormsg(t('Please Upload At Least One Gallery Image')); return }
      if (selectedProperty && noSubItems) {
        toasterrormsg(t(`This ${getCategoryLabel().toLowerCase()} has no ${getSubItemLabel().toLowerCase()}s. Please select another ${getCategoryLabel().toLowerCase()}.`))
        return
      }
      if (selectedProperty && subItemList.length > 0 && selectedSubItems.length === 0) {
        setSubItemError(t(`Please select at least one ${getSubItemLabel().toLowerCase()}`))
        return
      }

      setSaving(true)
      try {
        const actionTypeIdList = selectedSubItems.length > 0
          ? selectedSubItems.map((id) => String(id))
          : selectedProperty?.value ? [String(selectedProperty.value)] : []

        const completeSteps = { ...(adData?.completeSteps ?? {}), adsDetails: true }

        const detailSaveParam: Record<string, any> = {
          adminId,
          adsId,
          vendorId,
          en_name: values.en_name,
          ru_name: values.ru_name,
          uz_name: values.uz_name,
          en_description: values.en_description,
          ru_description: values.ru_description,
          uz_description: values.uz_description,
          promocode: values.promocode,
          actionType,
          actionTypeId: actionTypeIdList,
          completeSteps,
        }

        if (adsId > 0) {
          const oldActionId = adData?.actionDetail?.[0]?.actionId ?? null
          const oldActionLabel = adData?.actionDetail?.[0]?.en_parentName ?? adData?.actionDetail?.[0]?.en_name ?? ''

          const auditFields: Record<string, any> = {
            actionType: {
              old: { value: adData?.actionType ?? null, label: adData?.actionType ?? '' },
              new: { value: actionType, label: actionType },
            },
            actionId: {
              old: { value: oldActionId, label: oldActionLabel },
              new: { value: selectedProperty?.value ?? null, label: selectedProperty?.label ?? '' },
            },
          }

          if (subItemList.length > 0) {
            const oldIds: string[] = Array.isArray(adData?.actionTypeId) ? adData.actionTypeId.map(String) : []
            const oldOptions = oldIds.map((id) => {
              const match = initialSubItemListRef.current.find((s) => String(s.value) === id)
              return { value: id, label: match?.label ?? id }
            })
            const newOptions = actionTypeIdList.map((id) => {
              const match = subItemList.find((s) => String(s.value) === id)
              return { value: id, label: match?.label ?? id }
            })
            auditFields.actionTypeId = { old: oldOptions, new: newOptions, multi: true }
          }

          detailSaveParam.labels = buildDropdownAudit(auditFields)
        }

        const response = await fetchData('admin/ads/detail/save', detailSaveParam)

        if (String(response?.status) === '200') {
          toastsuccessmsg(response?.message || 'Details Saved')
          onSaveSuccess()
          goToNext()
        } else {
          toasterrormsg(response?.message || 'Failed To Save Details')
        }
      } catch (error) {
        console.error('Detail save error:', error)
        toasterrormsg('Failed To Save Details')
      } finally {
        setSaving(false)
      }
    },
  })

  const fetchSubItemsForProperty = useCallback(async (categoryId: number, propertyId: string | number) => {
    const catConfig = CATEGORY_TYPES.find((c) => c.value === categoryId)
    // No detail API (e.g. Cafe & Restaurant) — nothing to prefill, the property is the item.
    if (!catConfig?.detailApi) return

    try {
      // carRentalDetail / spaceDetails / tourpackageDetails expect the id as a string.
      const paramValue = /carRentalDetail|spaceDetails|tourpackageDetails/.test(catConfig.detailApi) ? String(propertyId) : propertyId
      const subItemParam = { [catConfig.parentIdKey]: paramValue }
      const subItemResponse = await fetchData(catConfig.detailApi, subItemParam)
      
      if (subItemResponse?.status == '200') {
        const detail = Array.isArray(subItemResponse?.data) ? subItemResponse.data[0] : subItemResponse?.data
        const items = Array.isArray(detail?.[catConfig.subKey]) ? detail[catConfig.subKey] : []

        if (items.length > 0) {
          const mapped = items.map((item: any) => ({
            value: item?.[catConfig.subIdKey] ?? item?.id ?? '',
            label: item?.[catConfig.nameKey] ?? item?.name ?? '-',
          }))
          setSubItemList(mapped)
          initialSubItemListRef.current = mapped
        }
      }
    } catch (error) {
      console.error('Error fetching sub-items for prefill:', error)
    }
  }, [])

  const fetchPropertiesForVendor = useCallback(async (categoryId: number, vId: number) => {
    try {
      if (!categoryId || !vId) return
      const catConfig = CATEGORY_TYPES.find((c) => c.value === categoryId)

      setPropertyLoading(true)
      const param: any = { vendorId: vId, categoryId, filter: false }
      const propertyResponse = await fetchData('admin/propertylist', param)

      if (propertyResponse?.status == '200') {
        const data = Array.isArray(propertyResponse?.data) ? propertyResponse.data : Array.isArray(propertyResponse?.data?.list) ? propertyResponse.data.list : []
        setVendorPropertyList(data.map((item: any) => ({
          // Prefer the id key the category's detail API expects, so the parent id we
          // send to that API (and store as actionId) always belongs to this category.
          value: (catConfig && item?.[catConfig.parentIdKey])
            ?? item?.hotelId ?? item?.spacerentalId ?? item?.spaceId ?? item?.tourpackageId
            ?? item?.carrentalId ?? item?.carId ?? item?.restaurantId ?? item?.value ?? '',
          label: item?.en_name ?? item?.hotelName ?? item?.name ?? item?.label ?? '-',
        })))
      }
    } catch (error) {
      console.error('Error fetching properties:', error)
    } finally {
      setPropertyLoading(false)
    }
  }, [])

  const prefillPromotionItems = useCallback(async (ad: any) => {
    if (!ad) return

    const catType = resolveCategoryType(ad)

    if (catType) {
      setSelectedCategoryType({ value: catType.value, label: catType.label })
    }

    if (ad?.categoryId && ad?.vendorId) {
      await fetchPropertiesForVendor(Number(ad.categoryId), ad.vendorId)
    }

    const actionDetails = Array.isArray(ad?.actionDetail) ? ad.actionDetail : []
    const actionId = actionDetails[0]?.actionId ?? ''
    const parentName = actionDetails[0]?.en_parentName ?? actionDetails[0]?.en_name ?? ''

    if (actionId) {
      const propertyOpt: SelectOption = { value: actionId, label: parentName || String(actionId) }
      setSelectedProperty(propertyOpt)

      if (catType) {
        await fetchSubItemsForProperty(catType.value, actionId)
      }

      const actionTypeIds = Array.isArray(ad?.actionTypeId) ? ad.actionTypeId : []
      if (actionTypeIds.length > 0) {
        setSelectedSubItems(actionTypeIds.map((id: any) => String(id)))
      }
    }
  }, [fetchPropertiesForVendor, fetchSubItemsForProperty])

  useEffect(() => {
    if (!adData) return
    formik.setValues({
      en_name: adData?.en_name ?? '',
      ru_name: adData?.ru_name ?? '',
      uz_name: adData?.uz_name ?? '',
      en_description: adData?.en_description ?? '',
      ru_description: adData?.ru_description ?? '',
      uz_description: adData?.uz_description ?? '',
      promocode: adData?.promocode ?? '',
    })
    setExistingMedia(
      Array.isArray(adData?.banners)
        ? adData.banners.map((b: any) => ({ adsmediaId: b?.adsmediaId ?? 0, image: b?.image ?? '' })).filter((b: any) => b.image)
        : []
    )

    prefillPromotionItems(adData)
  }, [adData])

  // The prefill only knows the saved actionId, so its label can fall back to that raw id.
  // Once the vendor's property list arrives, adopt the matching option itself so the real
  // name shows and react-select marks it as the selected item in the menu.
  useEffect(() => {
    if (!selectedProperty?.value || vendorPropertyList.length === 0) return
    const match = vendorPropertyList.find((p) => String(p.value) === String(selectedProperty.value))
    if (match && (match.value !== selectedProperty.value || match.label !== selectedProperty.label)) {
      setSelectedProperty(match)
    }
  }, [vendorPropertyList, selectedProperty])

  const handlePropertyChange = useCallback(async (opt: SelectOption | null) => {
    if (opt?.value && String(opt.value) === String(selectedProperty?.value)) return
    setSelectedProperty(opt)
    setSelectedSubItems([])
    setSubItemList([])
    setNoSubItems(false)
    setSubItemError('')

    if (!opt?.value || !selectedCategoryType?.value) return

    const categoryId = Number(selectedCategoryType.value)
    const catConfig = CATEGORY_TYPES.find((c) => c.value === categoryId)
    // Categories without sub-items (e.g. Cafe & Restaurant) promote the property directly:
    // skip the detail call entirely and leave the sub-item section hidden.
    if (!catConfig?.detailApi) return

    setSubItemLoading(true)
    try {
      
      const detailValue = /carRentalDetail|spaceDetails|tourpackageDetails/.test(catConfig.detailApi) ? String(opt.value) : opt.value
      const detailParam = { [catConfig.parentIdKey]: detailValue }
      const detailResponse = await fetchData(catConfig.detailApi, detailParam)
      if (detailResponse?.status == '200') {
        const detail = Array.isArray(detailResponse?.data) ? detailResponse.data[0] : detailResponse?.data
        const subList = Array.isArray(detail?.[catConfig.subKey]) ? detail[catConfig.subKey] : []

        if (subList.length > 0) {
          setSubItemList(
            subList.map((item: any) => ({
              value: item?.[catConfig.subIdKey] ?? item?.id ?? '',
              label: item?.[catConfig.nameKey] ?? item?.name ?? '-',
            }))
          )
        } else {
          setNoSubItems(true)
        }
      }
    } catch (error) {
      console.error('Error fetching property detail:', error)
    } finally {
      setSubItemLoading(false)
    }
  }, [selectedCategoryType, selectedProperty])

  // Gallery
  const compressImage = useCallback(async (file: File): Promise<File> => {
    try {
      const c = await imageCompression(file, { maxSizeMB: 2, maxWidthOrHeight: 1920, useWebWorker: true })
      return new File([c], `gallery_${Date.now()}_${file.name}`, { type: c.type })
    } catch { return file }
  }, [])

  const refreshMediaList = useCallback(async () => {
    if (!adsId) return
    try {
      const mediaListResponse = await fetchData('admin/ads/media/list', { adsId })
      if (mediaListResponse?.status == '200') {
        const list = Array.isArray(mediaListResponse?.data?.list)
          ? mediaListResponse.data.list
          : Array.isArray(mediaListResponse?.data) ? mediaListResponse.data : []
        setExistingMedia(
          list.map((b: any) => ({ adsmediaId: b?.adsmediaId ?? 0, image: b?.image ?? '' }))
            .filter((b: any) => b.image)
        )
      }
    } catch (error) {
      console.error('Error refreshing media:', error)
    }
  }, [adsId])

  const handleGalleryUpload = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (!files || files.length === 0) return
    const remaining = MAX_GALLERY - totalImages
    if (remaining <= 0) { toasterrormsg(`Maximum ${MAX_GALLERY} Images Allowed`); return }
    if (!adsId) { toasterrormsg('Please Complete Previous Steps First'); return }

    setMediaUploading(true)
    try {
      const toAdd = Array.from(files).slice(0, remaining)
      const compressed = await Promise.all(toAdd.map(compressImage))
      for (const file of compressed) {
        const fd = new FormData()
        fd.append('adminId', String(adminId))
        fd.append('adsId', String(adsId))
        fd.append('image', file)
        await fetchFormData('admin/ads/media/upload', fd)
      }
      await refreshMediaList()
    } catch { toasterrormsg('Failed To Upload Images') }
    finally { setMediaUploading(false) }
    if (e.target) e.target.value = ''
  }, [totalImages, adsId, adminId, compressImage, refreshMediaList])

  const deleteMedia = useCallback(async (adsmediaId: number) => {
    if (!adsmediaId) return
    try {
      const deleteResponse = await fetchData('admin/ads/media/delete', { adminId, adsmediaId }, 'DELETE')
      if (String(deleteResponse?.status) === '200') {
        setExistingMedia((prev) => prev.filter((m) => m.adsmediaId !== adsmediaId))
        toastsuccessmsg('Image Deleted')
      } else {
        toasterrormsg(deleteResponse?.message || 'Failed To Delete')
      }
    } catch {
      toasterrormsg('Failed To Delete Image')
    }
  }, [adminId])

  const toggleSubItem = useCallback((itemValue: string | number) => {
    setSubItemError('')
    setSelectedSubItems((prev) => {
      const id = String(itemValue)
      return prev.includes(id) ? prev.filter((v) => v !== id) : [...prev, id]
    })
  }, [])

  return (
    <>
      {/* Title */}
      <div className='mb-6'>
        <div className='d-flex align-items-center gap-2 mb-4'>
          <KTIcon iconName='text' className='fs-2 text-primary' />
          <h5 className='fw-bold mb-0'>{t('Advertisement Title')}</h5>
        </div>
        <div className='row g-4'>
          {(['en_name', 'ru_name', 'uz_name'] as const).map((fieldKey) => {
            const label = fieldKey === 'en_name' ? 'English' : fieldKey === 'ru_name' ? 'Russian' : 'Uzbek'
            return (
              <div className='col-12 col-md-4' key={fieldKey}>
                <label className='adform-label'>{t('Title')} ({label}) <span className='text-danger'>*</span></label>
                <input
                  type='text'
                  className={`form-control form-control-lg ${formik.touched[fieldKey] && formik.errors[fieldKey] ? 'is-invalid' : ''}`}
                  placeholder='Enter title'
                  maxLength={100}
                  name={fieldKey}
                  value={formik.values[fieldKey]}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
                <div className='d-flex justify-content-between mt-1'>
                  {formik.touched[fieldKey] && formik.errors[fieldKey] ? (
                    <span className='text-danger fs-8'>{formik.errors[fieldKey]}</span>
                  ) : <span />}
                  <span className='text-muted fs-9'>{(formik.values[fieldKey] || '').length}/100</span>
                </div>
              </div>
            )
          })}
        </div>
      </div>

      {/* Description */}
      <div className='mb-6'>
        <div className='d-flex align-items-center gap-2 mb-4'>
          <KTIcon iconName='document' className='fs-2 text-primary' />
          <h5 className='fw-bold mb-0'>{t('Main Description')}</h5>
        </div>
        {([
          { fieldKey: 'en_description' as const, label: 'English' },
          { fieldKey: 'ru_description' as const, label: 'Russian' },
          { fieldKey: 'uz_description' as const, label: 'Uzbek' },
        ]).map((f) => (
          <div className='mb-4' key={f.fieldKey}>
            <label className='adform-label'>{t('Description')} ({f.label}) <span className='text-danger'>*</span></label>
            <MemoizedEditor
              initialValue={formik.values[f.fieldKey]}
              editorConfig={editorConfig}
              onBlur={(content: string) => {
                formik.setFieldValue(f.fieldKey, content ?? '')
                formik.setFieldTouched(f.fieldKey, true)
              }}
            />
            <div className='d-flex justify-content-between mt-1'>
              {formik.touched[f.fieldKey] && formik.errors[f.fieldKey] ? (
                <span className='text-danger fs-8'>{formik.errors[f.fieldKey]}</span>
              ) : <span />}
              <span className='text-muted fs-9'>Maximum 500 characters</span>
            </div>
          </div>
        ))}
      </div>

      {/* Promotion Items — Cascading Dropdowns */}
      <div className='mb-6'>
        <div className='d-flex align-items-center gap-2 mb-3'>
          <KTIcon iconName='home-2' className='fs-2 text-primary' />
          <h5 className='fw-bold mb-0'>{t('Promotion Items')}</h5>
          <span className='badge badge-light-warning fs-9'>{t('Optional')}</span>
        </div>
        <p className='text-muted fs-7 mb-4'>{t('Select a category type, then choose the specific property to promote')}</p>

        <div className='row g-4'>
          {/* Step 2: Property */}
          {selectedCategoryType && (
            <div className='col-12 col-md-4'>
              <label className='adform-label'>{t(`Select ${getCategoryLabel()}`)}</label>
              <Select
                placeholder={propertyLoading ? t('Loading...') : t(`Search ${getCategoryLabel()}...`)}
                isSearchable
                isLoading={propertyLoading}
                options={vendorPropertyList}
                value={selectedProperty}
                onChange={(opt: any) => handlePropertyChange(opt)}
                classNamePrefix='adform-select'
                noOptionsMessage={() => propertyLoading ? t('Loading...') : t('No Results Found')}
              />
            </div>
          )}
        </div>

        {/* Sub-items as toggleable cards */}
        {selectedProperty && !subItemLoading && noSubItems && (
          <div className='mt-4'>
            <div className='alert alert-warning d-flex align-items-center gap-2 py-3'>
              <KTIcon iconName='information' className='fs-2 text-warning' />
              <span>{t(`This ${getCategoryLabel().toLowerCase()} has no ${getSubItemLabel().toLowerCase()}s available. Please select another ${getCategoryLabel().toLowerCase()}.`)}</span>
            </div>
          </div>
        )}
        {selectedProperty && subItemList.length > 0 && (
          <div className='mt-4'>
            <label className='adform-label mb-2'>
              {t(`Select ${getSubItemLabel()}`)} <span className='text-danger'>*</span>
              {subItemLoading && <span className='spinner-border spinner-border-sm ms-2' />}
            </label>
            <div className='adform-subitem-grid'>
              {subItemList.map((item) => {
                const isSelected = selectedSubItems.includes(String(item.value))
                return (
                  <div
                    key={String(item.value)}
                    className={`adform-subitem-card ${isSelected ? 'adform-subitem-card--selected' : ''}`}
                    onClick={() => toggleSubItem(item.value)}
                  >
                    <div className='adform-subitem-card__check'>
                      {isSelected ? (
                        <KTIcon iconName='check-circle' className='fs-2 text-primary' iconType='solid' />
                      ) : (
                        <div className='adform-subitem-card__unchecked' />
                      )}
                    </div>
                    <span className='adform-subitem-card__label'>{item.label}</span>
                  </div>
                )
              })}
            </div>
            {subItemError && (
              <div className='text-danger fs-8 mt-2'>{subItemError}</div>
            )}
            {selectedSubItems.length > 0 && (
              <div className='text-muted fs-8 mt-2'>
                {selectedSubItems.length} {t('selected')}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Gallery */}
      <div className='mb-6'>
        <div className='d-flex align-items-center gap-2 mb-3'>
          <KTIcon iconName='picture' className='fs-2 text-primary' />
          <h5 className='fw-bold mb-0'>{t('Gallery Images')} <span className='text-danger'>*</span></h5>
        </div>
        <input
          type='file'
          accept='image/jpeg,image/png,image/webp'
          multiple
          className='form-control form-control-lg mb-2'
          onChange={handleGalleryUpload}
          disabled={totalImages >= MAX_GALLERY || mediaUploading}
        />
        <div className='text-muted fs-8 mb-3'>
          JPG, PNG, WEBP. Max 2MB each. Maximum {MAX_GALLERY} images. ({totalImages}/{MAX_GALLERY})
          {mediaUploading && <span className='ms-2 text-primary'><span className='spinner-border spinner-border-sm me-1' />Uploading...</span>}
        </div>

        {existingMedia.length > 0 && (
          <div className='row g-3'>
            {existingMedia.map((m) => (
              <div className='col-4 col-md-2' key={m.adsmediaId}>
                <div className='adform-gallery-item'>
                  <img src={imageBaseUrl ? imageBaseUrl + m.image : ''} alt='Gallery' onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = toAbsoluteUrl('/media/logos/mainLogo.png') }} />
                  <button type='button' className='adform-gallery-item__remove' onClick={() => deleteMedia(m.adsmediaId)}>
                    <KTIcon iconName='cross' className='fs-5 text-white' iconType='solid' />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Promo Code */}
      <div className='mb-6'>
        <div className='d-flex align-items-center gap-2 mb-3'>
          <KTIcon iconName='discount' className='fs-2 text-primary' />
          <h5 className='fw-bold mb-0'>{t('Promo Code')}</h5>
          <span className='badge badge-light-warning fs-9'>{t('Optional')}</span>
        </div>
        <div className='col-12 col-md-6'>
          <input type='text' className='form-control form-control-lg' placeholder={t('Enter promo code')} maxLength={20} name='promocode' value={formik.values.promocode} onChange={formik.handleChange} onBlur={formik.handleBlur} />
          <span className='text-muted fs-9 mt-1'>{(formik.values.promocode || '').length}/20</span>
        </div>
      </div>

      {/* Save */}
      <div className='d-flex justify-content-end mt-6'>
        <button type='button' className='btn btn-primary d-flex align-items-center gap-2 px-5 py-3' onClick={() => formik.handleSubmit()} disabled={saving}>
          {saving ? <span className='spinner-border spinner-border-sm' /> : <KTIcon iconName='check' className='fs-3' />}
          <span>{t('Save & Next')}</span>
          <KTIcon iconName='arrow-right' className='fs-3' />
        </button>
      </div>
    </>
  )
}

export { AdsDetailStep }
